import styles from "./BlogsAll.module.scss";

import { Checkbox, Dropdown, Form, Layout, Modal, Radio, Select, Table } from "antd";
import { ApiService } from "api/api.service";
import PageTitle from "components/PageTitle";
import { useEffect, useState } from "react";
import { Flex, Image, Popconfirm, Tag, Typography, Button, message, Input } from "antd";
import { ColumnsType } from "antd/es/table";
import { API_URL } from "api";
import type { MenuProps, RadioChangeEvent } from "antd";
import TextNumeric from "components/TextNumeric";
import dayjs from "dayjs";
import { BlogDto, BlogSchemaDto, BlogStatusTypes, BlogVisibilityTypes } from "dtos/blog.dto";
import {
  TbChevronLeft,
  TbChevronRight,
  TbClipboardCheck,
  TbColumns3,
  TbCopy,
  TbDots,
  TbEdit,
  TbEye,
  TbFlare,
  TbLoader2,
  TbMail,
  TbPhone,
  TbPhoto,
  TbPlus,
  TbSearch,
  TbSortDescending,
  TbStatusChange,
  TbTrash,
} from "react-icons/tb";
import { Link, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { BLOG_STATUS_COLOURS, BLOG_VISIBILITY_COLOURS, LEAD_STATUS_COLOURS } from "utils/constants";
import { formatDate, formatDateTime, formatMoney } from "utils/functions";
import type { PopconfirmProps, TablePaginationConfig } from "antd";
import { T_Category, T_Category_Type } from "dtos/category.dto";
import DashboardLayout from "layout/DashboardLayout";
import { FilterValue } from "antd/es/table/interface";
import { LuListFilter } from "react-icons/lu";

export const BLOG_VISIBILITY_LABELS = {
  [BlogVisibilityTypes.HIDDEN]: "Hidden",
  [BlogVisibilityTypes.MEMBERSHIP_ONLY]: "Membership Only",
  [BlogVisibilityTypes.OPEN_TO_ALL]: "Open to All",
  [BlogVisibilityTypes.UNPUBLISHED]: "Unpublished",
};

type PopulatedBlogDto = NonNullable<ReturnType<typeof ApiService.blog.useGetAllPaginated>["data"]>["data"][number];

const BlogsAll = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<{
    page: number;
    limit: number;
    categories: string[];
    status?: string[];
    visibility?: string[];
    sorting: number;
  }>({
    page: Number(searchParams.get("page")) === 0 ? 1 : (Number(searchParams.get("page")) ?? 1),
    limit: Number(searchParams.get("limit")) === 0 ? 10 : (Number(searchParams.get("limit")) ?? 10),
    categories: [],
    status: [],
    visibility: [],
    sorting: -1,
  });
  const [selectedVisibility, setSelectedVisibility] = useState({ init: "", later: "" });
  const [selectedStatus, setSelectedStatus] = useState({ init: "", later: "" });
  const [selectedFeature, setSelectedFeature] = useState({ init: "", later: "" });
  const [openVisibilityModal, setOpenVisibilityModal] = useState(false);
  const [openStatusModal, setStatusModal] = useState(false);
  const [highlightedRow, setHighlightedRow] = useState<string | null>(null);
  const [openFeatureModal, setOpenFeatureModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBlog, setSelectedBlog] = useState("");
  const blogList = ApiService.blog.useGetAllPaginated({
    page: filters.page,
    limit: filters.limit,
    categories: filters.categories,
    status: filters.status,
    visibility: filters.visibility,
    searchString: searchTerm,
    sortOrder: filters.sorting,
  });
  const getAllCategories = ApiService.category.useGetAllPaginated({ page: 1, limit: 10 });
  const deleteBlog = ApiService.blog.useDeleteBySlugOrId();
  const updateStatus = ApiService.blog.useUpdateStatus();
  const updateVisibility = ApiService.blog.useUpdateVisibility();
  const updateFeature = ApiService.blog.useUpdateFeature();
  const [categoryForm] = Form.useForm();
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const items: MenuProps["items"] = [
    {
      label: "Change Visibility",
      key: "1",
      icon: <TbEye />,
    },
    {
      label: "Change Status",
      key: "2",
      icon: <TbStatusChange />,
    },
    {
      label: "Feature",
      key: "3",
      icon: <TbFlare />,
    },
  ];

  const handleRowUpdate = (rowId: string) => {
    setHighlightedRow(rowId);

    // Clear the highlight after 1 second
    setTimeout(() => {
      setHighlightedRow(null);
    }, 1000);
  };

  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    message.info("Click on left button.");
    console.log("click left button", e);
  };
  const handleDropDownSelect = (visibility: string, status: string, id: string, feature: boolean) => {
    // console.log(visibility, status);
    setSelectedVisibility({ init: visibility, later: visibility });
    setSelectedStatus({ init: status, later: status });
    setSelectedBlog(id);
    const featText = feature ? "Yes" : "No";
    setSelectedFeature({ init: featText, later: featText });
  };
  const formatTypes = (value: string) => {
    const formattedvalue = value.replace(/_/g, " ");
    return formattedvalue[0].toUpperCase() + formattedvalue.slice(1).toLowerCase();
  };
  const handleMenuClick: MenuProps["onClick"] = (e) => {
    // message.info("Click on menu item.");
    e.key === "1" ? setOpenVisibilityModal(true) : "";
    e.key === "2" ? setStatusModal(true) : "";
    e.key === "3" ? setOpenFeatureModal(true) : "";
  };
  const menuProps = {
    items,
    onClick: handleMenuClick,
  };
  const handleVisibilityChange = () => {
    updateVisibility.mutate(
      { id: selectedBlog, visiblity: selectedVisibility.later },
      {
        onSuccess: () => {
          message.success("Visibility updated successfully");
          blogList.refetch();
          setOpenVisibilityModal(false);
          handleRowUpdate(selectedBlog);
        },
      },
    );
  };
  const handleStatusChanged = () => {
    updateStatus.mutate(
      { id: selectedBlog, status: selectedStatus.later },
      {
        onSuccess: () => {
          message.success("Status updated successfully");
          blogList.refetch();
          setStatusModal(false);
          handleRowUpdate(selectedBlog);
        },
      },
    );
  };

  const handleFeatureChange = () => {
    updateFeature.mutate(
      { id: selectedBlog, feature: selectedFeature.later === "Yes" },
      {
        onSuccess: () => {
          message.success("Featured status changed successfully");
          blogList.refetch();
          setOpenFeatureModal(false);
          handleRowUpdate(selectedBlog);
        },
      },
    );
  };
  const confirm = (id: string) => {
    console.log("delete");
    deleteBlog.mutate(id, {
      onSuccess: () => {
        message.success("Blog deleted successfully");
        blogList.refetch();
      },
    });
  };

  const handleCategoryChange = (value: string[]) => {
    setFilters({ ...filters, categories: value });
  };

  const handleFilterByCategory = () => {
    setFilters((prev) => ({ ...prev, categories: selectedCategories }));
  };

  const handleSearch = () => {
    blogList.refetch();
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    keyFilters: Record<string, any[] | null>,
    sorter: any,
  ) => {
    const statusFilters = keyFilters.status ?? [];
    const visibilityFilters = keyFilters.visiblity ?? [];
    const sort = sorter.order ? (sorter.order === "ascend" ? 1 : -1) : 1;
    console.log(sort);

    setFilters({
      page: pagination.current ?? 1,
      limit: pagination.pageSize ?? 10,
      status: statusFilters,
      visibility: visibilityFilters,
      categories: filters.categories,
      sorting: sort,
    });
  };

  const cancel: PopconfirmProps["onCancel"] = (e) => {};

  const [columnsHidden, setColumnsHidden] = useState<{ [k in keyof BlogSchemaDto]?: boolean }>({
    comments: true,
    updatedAt: true,
    createdAt: true,
    tags: true,
  });

  const columns: ColumnsType<PopulatedBlogDto> = [
    {
      key: "blog",
      title: "Blog",
      width: 400,
      fixed: "left",
      // ellipsis: true,
      render: (record: PopulatedBlogDto) => {
        return (
          <div>
            <Typography.Paragraph className="!m-0" type="secondary">
              <Link className="font-semibold hover:!underline" to={`/dashboard/blog/${record.slug}`}>
                {record.title}
              </Link>
            </Typography.Paragraph>
          </div>
        );
      },
    },
    {
      key: "published_at",
      dataIndex: "published_at",
      title: "Published at",
      width: 200,
      render: (_, record: PopulatedBlogDto) => {
        return (
          <TextNumeric>
            <Typography.Text type="secondary">
              <small>{record?.published_at ? formatDateTime(record?.published_at) : "-"}</small>
            </Typography.Text>
          </TextNumeric>
        );
      },
    },
    {
      key: "status",
      dataIndex: "status",
      title: "Status",
      width: 150,
      render: (status: PopulatedBlogDto["status"]) => {
        return <Tag color={BLOG_STATUS_COLOURS[status]}>{status[0] + status.slice(1).toLocaleLowerCase()}</Tag>;
      },
    },
    {
      key: "visiblity",
      dataIndex: "visiblity",
      title: "Visibility",
      width: 200,
      render: (value: string) => {
        const visibilityType = value as BlogVisibilityTypes;
        const type = visibilityType.replace(/_/g, " ");
        return <Tag color={BLOG_VISIBILITY_COLOURS[visibilityType]}>{BLOG_VISIBILITY_LABELS[visibilityType]}</Tag>;
      },
    },
    {
      title: "Categories",
      key: "categories",
      width: 250,
      render: (text, record) => <>{record?.categories?.map((category, i) => <Tag key={i}>{category.title}</Tag>)}</>,
    },
    {
      title: "Tags",
      key: "tags",
      width: 250,
      render: (text, record) => (
        <Typography.Text type="secondary" className="text-xs">
          {record?.tags?.map((tag) => `#${tag}`).join(" ")}
        </Typography.Text>
      ),
    },
    {
      title: "Featured",
      key: "featured",
      width: 150,
      render: (text, record) => <>{record.featured ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>}</>,
    },
    {
      title: "Author",
      dataIndex: "author",
      key: "author",
      render: (author) => <span>{author.name}</span>,
    },
    {
      title: "Comments",
      dataIndex: "comments",
      key: "comments",
      render: (comments) => comments.length,
      width: 120,
    },
    {
      key: "createdAt",
      dataIndex: "createdAt",
      title: "Created",
      width: 150,
      // ellipsis: true,
      render: (createdAt: PopulatedBlogDto["createdAt"]) => {
        return (
          <TextNumeric>
            <Typography.Text type="secondary">
              <small>{createdAt ? formatDate(createdAt) : "-"}</small>
            </Typography.Text>
          </TextNumeric>
        );
      },
    },
    {
      key: "updatedAt",
      dataIndex: "updatedAt",
      title: "Updated",
      width: 150,
      // ellipsis: true,
      render: (updatedAt: PopulatedBlogDto["updatedAt"]) => {
        return (
          <TextNumeric>
            <Typography.Text type="secondary">
              <small>{updatedAt ? formatDate(updatedAt) : "-"}</small>
            </Typography.Text>
          </TextNumeric>
        );
      },
    },
    {
      title: "",
      dataIndex: "action",
      key: "action",
      // width: 80,
      fixed: "right",
      render: (name: string, record: PopulatedBlogDto, index: number) => {
        return (
          <Flex justify="center" align="center" gap={2}>
            {/* <Button
              onClick={() => navigate(`/dashboard/blog/${record.slug}?page=${filters.page}&limit=${filters.limit}`)}
              icon={<TbEdit style={{ fontSize: "20px" }} />}
              type="text"
            />

            <Popconfirm
              title="Delete the blog"
              description="Are you sure to delete this blog?"
              onConfirm={() => confirm(record._id)}
              onCancel={cancel}
              okButtonProps={{}}
              okText="Yes"
              cancelText="No"
            >
              <Button type="text">
                <TbTrash style={{ color: "red", fontSize: "20px" }} />
              </Button>
            </Popconfirm> */}
            <Dropdown trigger={["click"]} menu={menuProps}>
              <Button
                onClick={() => handleDropDownSelect(record.visiblity, record.status, record._id, record.featured)}
                icon={<TbDots />}
                type="text"
              />
            </Dropdown>
          </Flex>
        );
      },
    },
  ];
  useEffect(() => {
    if (searchParams.has("cat")) {
      const newCategory = searchParams.get("cat") as string;
      if (!filters.categories.includes(newCategory)) {
        setFilters((prev) => ({ ...prev, categories: [newCategory] }));
      }
    }
  }, [searchParams]);

  useEffect(() => {
    let getSearchData: NodeJS.Timeout | undefined;
    if (searchTerm) {
      getSearchData = setTimeout(() => {
        blogList.refetch();
      }, 2000);
    }

    return () => {
      if (getSearchData) clearTimeout(getSearchData);
    };
  }, [searchTerm]);

  return (
    <DashboardLayout
      headerLeft={
        <Flex gap={12}>
          <Typography.Title level={5} className="!m-0">
            All Blogs{" "}
          </Typography.Title>
          <Typography.Title level={5} className="!m-0 !font-bold" type="secondary">
            {blogList.data?.totalObjects}
          </Typography.Title>
        </Flex>
      }
      headerRight={
        <Button
          type="primary"
          onClick={() => {
            navigate(`/dashboard/create-blog`);
          }}
          icon={<TbPlus />}
        >
          Create Blog
        </Button>
      }
    >
      <PageTitle title="Blogs" pageTitle={[{ title: "Blogs" }]} />

      <Table
        // className="border border-gray-300 rounded-xl overflow-hidden"
        virtual={false}
        loading={{
          spinning: blogList.isFetching,
          indicator: <TbLoader2 size={48} className="text-brand-500 animate-spin" />,
        }}
        columns={columns
          .map((col) => {
            if (
              Object.keys(columnsHidden).includes(col.key as string) &&
              (columnsHidden as any)[col.key as any] === true
            ) {
              return null;
            } else return col;
          })
          .filter((col) => !!col)}
        dataSource={blogList.data?.data}
        // onChange={handleTableChange}
        rowKey={"_id"} // Add this line
        pagination={{
          hideOnSinglePage: true,
          showQuickJumper: true,
          showPrevNextJumpers: true,
          nextIcon: (
            <Button
              className="ant-pagination-item-link !flex items-center justify-center"
              type="text"
              icon={<TbChevronRight size={20} />}
            />
          ),
          prevIcon: (
            <Button
              className="ant-pagination-item-link !flex items-center justify-center"
              type="text"
              icon={<TbChevronLeft size={20} />}
            />
          ),
          showTitle: true,
          showTotal: (total, range) => {
            return `Showing ${range[0]}-${range[1]} of ${total} blogs`;
          },
          showSizeChanger: true,
          current: filters.page,
          total: blogList.data?.totalObjects,
          pageSize: filters.limit,
          onChange: (page, pageSize) => {
            setFilters((prev) => ({ ...prev, page, limit: pageSize, sorting: -1 }));
          },
        }}
        rowClassName={(record) => (record._id === highlightedRow ? "highlighted-row" : "")}
        title={() => {
          return (
            <Form form={categoryForm}>
              <Form.Item<BlogDto> name={"categories"} style={{ marginBottom: "0px" }}>
                <div className="flex justify-between">
                  <div className="inline-flex gap-2">
                    {/* {!searchParams.has("cat") && (
                        <Select
                          loading={getAllCategories.isLoading}
                          options={
                            getAllCategories.data?.data
                              ? getAllCategories.data.data
                                  .filter((cat: T_Category) => cat.type === T_Category_Type.BLOG)
                                  .map((cat: T_Category) => {
                                    return { label: cat.title, value: cat._id };
                                  })
                              : []
                          }
                          mode="multiple"
                          placeholder="Filter by category"
                          onChange={handleCategoryChange}
                          style={{ width: "200px" }}
                        />
                      )} */}
                    <Dropdown
                      menu={{
                        items: columns
                          .filter((el) => !["blog", "action"].includes(el.key as string))
                          .map((el) => {
                            const key = String(el.key);
                            return {
                              key: key,
                              label: (
                                <div className="flex gap-2" key={el.key}>
                                  <Checkbox checked={!(columnsHidden as any)[key]} />
                                  <span>{el.title?.toString()}</span>
                                </div>
                              ),
                              onClick: () => {
                                setColumnsHidden((prev) => ({ ...prev, [key]: !(prev as any)[key] }));
                              },
                            };
                          }),
                      }}
                    >
                      <Button icon={<TbColumns3 />}>
                        Columns{" "}
                        <Tag bordered={false} className="!m-0 !bg-gray-100 !font-medium">
                          {
                            columns.filter((el) => {
                              if (["blog", "action"].includes(el.key as string)) return false;
                              else if ((el.key as string) in (columnsHidden as any)) return false;
                              else return true;
                            }).length
                          }
                        </Tag>
                      </Button>
                    </Dropdown>
                    {/* <Button icon={<TbSortDescending />}>Sort</Button>
                    <Button icon={<LuListFilter />}>Filter</Button> */}
                  </div>
                  <Input
                    placeholder="Search blogs"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ width: "200px" }}
                    prefix={<TbSearch className="stroke-gray-400" />}
                    onPressEnter={handleSearch}
                    allowClear
                    onClear={handleSearch}
                    // suffix={blogList.isRefetching ? <TbLoader2 className="text-gray-300 animate-spin" /> : null}
                  />
                </div>
              </Form.Item>
            </Form>
          );
        }}
        scroll={{ y: "calc(100vh - 64px - 48px - 64px - 64px)", x: "max-content" }}
        // footer={() => {
        //   return (
        //     <div>
        //       Showing {filters.limit} of {blogList.data?.totalObjects} blogs
        //     </div>
        //   );
        // }}
      />

      <Modal
        title={`Change visibility`}
        open={openVisibilityModal}
        onOk={handleVisibilityChange}
        okText={"Update"}
        onCancel={() => setOpenVisibilityModal(false)}
        okButtonProps={{
          disabled: selectedVisibility.init === selectedVisibility.later,
          loading: updateVisibility.isPending,
        }}
      >
        <Radio.Group
          value={selectedVisibility.later}
          onChange={(e: RadioChangeEvent) => setSelectedVisibility({ ...selectedVisibility, later: e.target.value })}
          options={Object.values(BlogVisibilityTypes).map((type) => ({
            label: formatTypes(type),
            value: type,
          }))}
        />
      </Modal>

      <Modal
        title={`Change Status`}
        open={openStatusModal}
        okText={"Update"}
        onOk={handleStatusChanged}
        onCancel={() => setStatusModal(false)}
        okButtonProps={{ disabled: selectedStatus.init === selectedStatus.later, loading: updateStatus.isPending }}
      >
        <Radio.Group
          value={selectedStatus.later}
          onChange={(e: RadioChangeEvent) => setSelectedStatus({ ...selectedStatus, later: e.target.value })}
          options={Object.values(BlogStatusTypes).map((type) => ({
            label: formatTypes(type),
            value: type,
          }))}
        />
      </Modal>
      <Modal
        title={`Do you want this blog to be featured?`}
        open={openFeatureModal}
        onOk={handleFeatureChange}
        okText={"Update"}
        onCancel={() => setOpenFeatureModal(false)}
        okButtonProps={{
          disabled: selectedFeature.init === selectedFeature.later,
          loading: updateFeature.isPending,
        }}
      >
        <Radio.Group
          value={selectedFeature.later}
          onChange={(e: RadioChangeEvent) => setSelectedFeature({ ...selectedFeature, later: e.target.value })}
          options={[
            { label: "Yes", value: "Yes" },
            { label: "No", value: "No" },
          ]}
        />
      </Modal>
    </DashboardLayout>
  );
};

export default BlogsAll;
